import React, { useState, useEffect, Suspense, lazy } from "react";
import {
  LayoutGrid,
  Kanban,
  ListChecks,
  Timer,
  Table,
  Settings2,
  LogOut,
  Shield,
  Phone,
  Bell,
  RotateCcw,
  Target,
  LucideIcon,
  Send,
  FileText,
  Star,
  TrendingUp,
  DollarSign,
  Users,
  UserCog,
  Search,
  MessageCircle,
  MapPin,
  Gauge,
  MousePointerClick,
  Rocket,
  BriefcaseBusiness,
  HandCoins,
  BookOpenCheck,
  CalendarCheck,
} from "lucide-react";
import BrandMark from "@/components/brand/BrandMark";
import { NavLink, useLocation } from "react-router-dom";

import { canManageLeads, canAccessConfiguracoes, canManageConexoesWhatsapp } from "@/lib/roles-permissions";
import { sistemaDeTrabalho } from "@/lib/sistemaDeTrabalho";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { verificarDestinatario } from "@/hooks/useAvisosUsuario";
import { Badge } from "@/components/ui/badge";
import { useRecadosAbertos } from "@/hooks/useMcpAdmin";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
const EvolutionConnectionDialog = lazy(() => import("@/components/whatsapp-inbox/EvolutionConnectionDialog"));
// useModoFocoCount removido — modo foco desativado (Douglas 10/06)

interface MenuItem {
  title: string;
  url: string;
  icon: LucideIcon;
  color: string;
  badgeKey?: 'avisosNaoLidos' | 'filaAtiva' | 'whatsappInboxUnread' | 'recadosAbertos';
}

interface MenuCategory {
  label: string;
  items: MenuItem[];
}

// Função para gerar menu baseado na role do usuário
const getMenuCategories = (
  isMaster: boolean,
  canManage: boolean,
  isGerenteOuMaster: boolean = false,
  isConsultor: boolean = false,
  isSupervisor: boolean = false,
  podeAcessarUsuarios: boolean = false,
  rotuloUsuarios: string = "Usuários",
  isDiretor: boolean = false,
  isBko: boolean = false,
  podeAcessarConfig: boolean = false,
  podeGerirConexoes: boolean = false,
  podeBoraFazer: boolean = false,
  isQualidade: boolean = false,
  podeAcessarRh: boolean = false,
  veConectaIa: boolean = false,
): MenuCategory[] => [
  {
    label: "Dashboards",
    items: [
      { title: "Produtividade", url: "/visao-comissoes", icon: DollarSign, color: "text-sidebar-foreground/70" },
      ...(isGerenteOuMaster || isDiretor || isSupervisor ? [{ title: "Controle Diário", url: "/controle-diario", icon: Gauge, color: "text-sidebar-foreground/70" }] : []),
      { title: "Ranking", url: "/dashboards?tab=gerencial", icon: LayoutGrid, color: "text-sidebar-foreground/70" },
      { title: "Visão Prime", url: "/dashboards?tab=visao-prime", icon: Star, color: "text-sidebar-foreground/70" },
      { title: "Visão Conecta", url: "/dashboards?tab=visao-conecta", icon: Star, color: "text-sidebar-foreground/70" },
    ]
  },
  {
    label: "Comercial",
    items: [
      { title: "WhatsApp Inbox", url: "/whatsapp-inbox", icon: MessageCircle, color: "text-sidebar-foreground/70", badgeKey: 'whatsappInboxUnread' as const },
      // ⛔ "Catálogo" SAIU DO MENU — Douglas, 18/09/2026: "tire a página catálogo
      // agora, ela já existe em configurações, os consultores e ninguém mais
      // precisa, está no painel agora". As artes e os books abrem no Painel CRM;
      // a gestão administra em Configurações → Catálogo.
      { title: "Discadora", url: "/discadora", icon: Phone, color: "text-sidebar-foreground/70", badgeKey: 'filaAtiva' },
      { title: "Leads", url: "/comercial?tab=prospeccao", icon: Target, color: "text-sidebar-foreground/70" },
      { title: "Carteira", url: "/carteira?visao=clientes&origem=comercial", icon: BriefcaseBusiness, color: "text-sidebar-foreground/70" },
      ...(isGerenteOuMaster || isDiretor || isSupervisor || isConsultor ? [{ title: "Performance Leads", url: "/performance-leads", icon: BarChart3, color: "text-sidebar-foreground/70" }] : []),
      ...(veConectaIa ? [{ title: "Conecta IA", url: "/conecta-ia", icon: Bot, color: "text-sidebar-foreground/70" }] : []),
      { title: "Click", url: "/comercial?tab=cliques", icon: MousePointerClick, color: "text-sidebar-foreground/70" },
      // Fila de Oportunidades — so' a QUALIDADE. Hoje todo bko/qualidade ativo
      // esta' nessa equipe (conferido no banco em 30/08/2026); a checagem que
      // vale e' a do backend, que olha a EQUIPE e nao o papel.
      ...(isQualidade ? [{ title: "Oportunidades", url: "/comercial?tab=oportunidades", icon: HandCoins, color: "text-sidebar-foreground/70" }] : []),
      { title: "Busca 360", url: "/busca-lead-360", icon: Search, color: "text-sidebar-foreground/70" },
      { title: "Planilha", url: "/planilha", icon: Table, color: "text-sidebar-foreground/70" },
      { title: "Kanban", url: "/kanban", icon: Kanban, color: "text-sidebar-foreground/70" },
      // As mesmas filas do Kanban, com porta própria: quem trabalha uma triagem
      // não deveria precisar achar a coluna certa antes (Douglas 22/09/2026).
      // A casa de quem trabalha pedido e a Fila da equipe da gestão (Douglas 24/10/2026).
      { title: "Minha fila", url: "/minha-fila", icon: Timer, color: "text-sidebar-foreground/70" },
      { title: "Triagens", url: "/triagens", icon: ListChecks, color: "text-sidebar-foreground/70" },
      { title: "Tarefas", url: "/tarefas", icon: CalendarCheck, color: "text-sidebar-foreground/70" },
    ]
  },
  {
    label: "Operacional",
    items: [
      ...(canManage ? [{ title: "Gestão de Leads", url: "/comercial?tab=gestao-leads", icon: Send, color: "text-sidebar-foreground/70" }] : []),
      ...(!isConsultor && !isSupervisor ? [{ title: "Backoffice Prime", url: "/dashboards?tab=slas", icon: Shield, color: "text-sidebar-foreground/70" }] : []),
      // 🆕 Minha Performance — autosserviço pra BKO ver a própria nota mensal
      ...(isBko ? [{ title: "Minha Performance", url: "/minha-performance", icon: Star, color: "text-sidebar-foreground/70" }] : []),
      { title: "Faturas", url: "/comercial?tab=faturas", icon: FileText, color: "text-sidebar-foreground/70" },
      ...(isGerenteOuMaster || isDiretor || isSupervisor ? [{ title: "Ciclo de Faturas", url: "/ciclo-faturas", icon: Receipt, color: "text-sidebar-foreground/70" }] : []),
      ...(canManage ? [{ title: "Mailing", url: "/mailing-diarios", icon: FileText, color: "text-sidebar-foreground/70" }] : []),

      { title: "Cobertura", url: "/cobertura", icon: MapPin, color: "text-sidebar-foreground/70" },
      ...(isGerenteOuMaster ? [{ title: "Disparos", url: "/email-marketing", icon: TrendingUp, color: "text-sidebar-foreground/70" }] : []),
    ]
  },
  ...(podeAcessarRh ? [{
    label: "Pessoas",
    items: [
      { title: "RH & Pessoas", url: "/rh?tab=semana", icon: BookOpenCheck, color: "text-sidebar-foreground/70" },
    ],
  }] : []),
  {
    label: "Sistema",
    items: [
      // 🔒 Gestão e Configurações apenas para MASTER
      
      { title: "Avisos", url: "/avisos", icon: Bell, color: "text-sidebar-foreground/70", badgeKey: 'avisosNaoLidos' },
      ...(podeAcessarUsuarios ? [{ title: rotuloUsuarios, url: "/usuarios", icon: UserCog, color: "text-sidebar-foreground/70" }] : []),
      ...(isGerenteOuMaster ? [{ title: "Equipes", url: "/equipes", icon: Users, color: "text-sidebar-foreground/70" }] : []),
      ...(podeAcessarConfig || podeGerirConexoes ? [{ title: "Configurações", url: "/configuracoes", icon: Settings2, color: "text-sidebar-foreground/70" }] : []),
      // Projeto Bora Fazer: só quem está no time do projeto (+ gestão) enxerga.
      ...(podeBoraFazer ? [{ title: "Bora Fazer", url: "/bora-fazer", icon: Rocket, color: "text-sidebar-foreground/70" }] : []),
    ]
  }
];

import { useMinhaConexaoSaude } from "@/hooks/useMinhaConexaoSaude";
import { useBoraFazerAcesso } from "@/hooks/useBoraFazer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Bot, Smartphone, LifeBuoy, QrCode, BarChart3, Receipt } from "lucide-react";

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const { signOut, profile } = useAuth();
  const conexaoSaude = useMinhaConexaoSaude(profile?.id);
  const [reconnectOpen, setReconnectOpen] = useState(false);
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const collapsed = state === "collapsed";
  
  // ✅ Função para verificar se item está ativo (suporta query params)
  const isItemActive = (itemUrl: string): boolean => {
    // Separar pathname e query do item
    const [itemPath, itemQuery] = itemUrl.split('?');
    
    // Se pathname não bate, não está ativo
    if (currentPath !== itemPath && !currentPath.startsWith(itemPath + '/')) {
      return false;
    }
    
    // Se item tem query params, verificar se estão presentes
    if (itemQuery) {
      const itemParams = new URLSearchParams(itemQuery);
      const currentParams = new URLSearchParams(currentSearch);
      
      // Verificar se o tab do item bate com o tab atual
      const itemTab = itemParams.get('tab');
      const currentTab = currentParams.get('tab');
      
      if (itemTab && itemTab !== currentTab) return false;
    }
    
    // Se item NÃO tem query e pathname é /comercial, 
    // não marcar como ativo (outros items mais específicos devem ter prioridade)
    if (!itemQuery && currentPath === '/comercial' && currentSearch) {
      return false;
    }
    
    return true;
  };
  
  // Badge Modo Foco removido (10/06)
  
  // 🔒 Gerar menu baseado na role
  const isMaster = profile?.role === 'master';
  const isGerenteOuMaster = profile?.role === 'master' || profile?.role === 'gerente' ||
    profile?.roles?.includes('master') || profile?.roles?.includes('gerente');
  const canManage = canManageLeads(profile?.role);
  const isConsultor = profile?.role === 'consultor' || profile?.roles?.includes('consultor');
  const isSupervisor = profile?.role === 'supervisor' || profile?.roles?.includes('supervisor');
  const isDiretor = profile?.role === 'diretor' || profile?.roles?.includes('diretor');

  // Aba Usuários: master/diretor/gerente veem "Usuários" (admin total).
  // Supervisor/coordenador veem "Minha Equipe" (escopo restrito à própria equipe).
  const rolesAdminUsuarios = ['master', 'diretor', 'gerente'];
  const rolesEscopoEquipe = ['supervisor', 'coordenador'];
  const isAdminUsuarios =
    rolesAdminUsuarios.includes(profile?.role ?? '') ||
    profile?.roles?.some((r) => rolesAdminUsuarios.includes(r));
  const isEscopoEquipe =
    rolesEscopoEquipe.includes(profile?.role ?? '') ||
    profile?.roles?.some((r) => rolesEscopoEquipe.includes(r));
  const podeAcessarUsuarios = !!(isAdminUsuarios || isEscopoEquipe);
  const rotuloUsuarios = isAdminUsuarios ? 'Usuários' : 'Minha Equipe';

  const isBko = profile?.role === 'bko' || profile?.roles?.includes('bko');
  // Configurações: master/gerente (já tinham) + supervisor + qualidade (liberado a pedido).
  const podeAcessarConfig = canAccessConfiguracoes(profile?.role) ||
    !!profile?.roles?.some(canAccessConfiguracoes);
  // Conexões WhatsApp: gestão completa pra master/diretor/gerente/supervisor.
  const podeGerirConexoes = canManageConexoesWhatsapp(profile?.role) ||
    !!profile?.roles?.some(canManageConexoesWhatsapp);
  // Projeto Bora Fazer: quem enxerga vem do backend (time do projeto + gestão).
  const { data: acessoBoraFazer } = useBoraFazerAcesso();
  const podeBoraFazer = !!acessoBoraFazer?.pode;
  const isQualidade = isBko
    || profile?.role === 'qualidade' || profile?.roles?.includes('qualidade')
    || ['master', 'diretor', 'gerente'].includes(String(profile?.role || ''));
  const podeAcessarRh =
    ['master', 'diretor', 'gerente'].includes(String(profile?.role || '')) ||
    profile?.roles?.some((role) => ['master', 'diretor', 'gerente'].includes(role)) ||
    profile?.funcao === 'RH_DEPARTAMENTO_PESSOAL';
  // Conecta IA (Douglas 29/09/2026): gestão e a liderança da equipe CPF (a
  // supervisora da Conecta). Mesma consulta do BrandProvider, então vem do cache.
  const { data: equipeSidebar } = useQuery({
    queryKey: ['brand-equipe', profile?.id, profile?.equipe_id],
    queryFn: async () => {
      const { data, error } = await supabase.from('equipes')
        .select('id, tipo_pessoa').eq('id', profile!.equipe_id!).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.ativo && !!profile?.equipe_id,
    staleTime: 30_000,
  });
  const veConectaIa = !!(isGerenteOuMaster || isDiretor || profile?.funcao === 'SUPERVISORA_BACKOFFICE'
    || (isEscopoEquipe && equipeSidebar?.tipo_pessoa === 'PF'));
  const menuCategories = getMenuCategories(
    isMaster,
    canManage,
    isGerenteOuMaster,
    isConsultor,
    isSupervisor,
    podeAcessarUsuarios,
    rotuloUsuarios,
    isDiretor,
    isBko,
    podeAcessarConfig,
    podeGerirConexoes,
    podeBoraFazer,
    isQualidade,
    podeAcessarRh,
    veConectaIa,
  );

  // 🎯 Sistema de trabalho (Douglas 22/10/2026): BKO e analista veem só o menu
  // da função, na ordem original do CRM. A Oportunidades segue para quem é da
  // equipe QUALIDADE (Lisiane 01/09/2026: Thuane e Caroline precisam da aba).
  const menuDoTrabalho = sistemaDeTrabalho(profile?.funcao);
  const veMinhaFila = !!menuDoTrabalho || isGerenteOuMaster || isDiretor || profile?.funcao === 'SUPERVISORA_BACKOFFICE';
  const menuCategoriesFiltered = (menuDoTrabalho
    ? menuCategories
        .map((cat) => ({
          ...cat,
          items: cat.items.filter((it) => menuDoTrabalho.menu.includes(it.title)),
        }))
        .filter((cat) => cat.items.length > 0)
    : menuCategories)
    .map((cat) => ({ ...cat, items: cat.items.filter((it) => veMinhaFila || it.title !== 'Minha fila') }))
    .filter((cat) => cat.items.length > 0);

  // ✅ SmartPolling: reage dinamicamente à visibilidade da aba
  const [isTabVisible, setIsTabVisible] = useState(document.visibilityState === 'visible');
  useEffect(() => {
    const handler = () => setIsTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Avisos pendentes DO usuário (destinatário + não lido / não confirmado)
  const { data: avisosNaoLidos = 0 } = useQuery({
    queryKey: ['sidebar-avisos-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      const [ativosRes, leiturasRes] = await Promise.all([
        supabase.from('v_avisos_ativos').select('*'),
        supabase.from('avisos_leituras').select('aviso_id, confirmado').eq('usuario_id', profile.id),
      ]);
      const leituraPorAviso = new Map((leiturasRes.data || []).map((l: any) => [l.aviso_id, l]));
      return (ativosRes.data || []).filter((a: any) => {
        if (!verificarDestinatario(a, profile.id, profile.role || '', profile.equipe_id || undefined)) return false;
        const l = leituraPorAviso.get(a.id);
        if (!l) return true;
        return !!a.requer_confirmacao && !l.confirmado;
      }).length;
    },
    enabled: !!profile?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchInterval: isTabVisible ? 5 * 60 * 1000 : false, // ✅ pausa quando aba em background
    refetchOnWindowFocus: false,
  });

  // Buscar fila ativa da discadora
  const { data: filaAtiva } = useQuery({
    queryKey: ['sidebar-fila-ativa', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      const { data } = await supabase
        .from('discadora_status_agente')
        .select('fila_atual_nome')
        .eq('usuario_id', profile.id)
        .maybeSingle();
      return data?.fila_atual_nome || null;
    },
    enabled: !!profile?.id,
    staleTime: 60 * 1000, // 1 minuto
    refetchInterval: isTabVisible ? 60 * 1000 : false, // ✅ pausa quando aba em background
    refetchOnWindowFocus: false,
  });

  // Buscar unread count do WhatsApp Inbox
  const { data: whatsappInboxUnread = 0 } = useQuery({
    queryKey: ['sidebar-whatsapp-inbox-unread', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      const isManager = profile.role === 'master' || profile.role === 'gerente';
      let query = supabase
        .from('whatsapp_conversations')
        .select('unread_count')
        .gt('unread_count', 0)
        .not('status', 'in', '("finalizada","arquivada")');
      if (!isManager) query = query.eq('owner_user_id', profile.id);
      const { data } = await query;
      return (data || []).reduce((sum: number, c: any) => sum + (c.unread_count || 0), 0);
    },
    enabled: !!profile?.id,
    staleTime: 30_000,
    refetchInterval: isTabVisible ? 2 * 60_000 : false,
    refetchOnWindowFocus: false,
  });

  const { data: recados } = useRecadosAbertos(!!isMaster);
  const recadosAbertos = recados?.abertos ?? 0;

  const getBadgeValue = (badgeKey?: MenuItem['badgeKey']): string | null => {
    if (!badgeKey) return null;
    switch (badgeKey) {
      case 'avisosNaoLidos':
        return avisosNaoLidos > 0 ? (avisosNaoLidos > 99 ? '99+' : String(avisosNaoLidos)) : null;
      case 'filaAtiva':
        return filaAtiva ? '●' : null;
      // case 'modoFocoCount' removido (10/06)
      case 'whatsappInboxUnread':
        return whatsappInboxUnread > 0 ? (whatsappInboxUnread > 99 ? '99+' : String(whatsappInboxUnread)) : null;
      // O recado pedido pelo time aparece como NÚMERO, igual ao resto da casa.
      // Nada de popup: os 48 da LETICIA sumiram por falta de um número na tela,
      // não por falta de alarme no meio do trabalho.
      case 'recadosAbertos':
        return recadosAbertos > 0 ? (recadosAbertos > 99 ? '99+' : String(recadosAbertos)) : null;
      default:
        return null;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  return (
    <Sidebar
      className={cn(
        "border-r border-sidebar-border transition-all duration-300 flex flex-col h-full",
        collapsed ? "w-16" : "w-[230px]"
      )}
      collapsible="offcanvas"
    >
      {/* Header com Logo */}
      <SidebarHeader className="px-3.5 py-2 border-b border-sidebar-border bg-sidebar-background flex-shrink-0">
        <div className="flex items-center justify-center">
          <BrandMark
            className={collapsed ? undefined : "w-full justify-center"}
            collapsed={collapsed}
            onDark
            imgClassName={
              collapsed
                ? "h-9 w-9 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
                : "h-auto w-[184px] max-w-full object-contain"
            }
          />
        </div>
      </SidebarHeader>

      {/* Conteúdo Principal - SEM ROLAGEM */}
      <SidebarContent className="px-2 py-1 flex flex-col flex-1">
        {menuCategoriesFiltered.map((category, categoryIndex) => (
          <SidebarGroup key={category.label} className={cn("p-0", categoryIndex > 0 && "mt-[7px]")}>
            {/* Label da Categoria */}
            {!collapsed && (
              <SidebarGroupLabel className="h-auto px-[9px] py-0.5 text-[8.5px] font-bold leading-[1.2] tracking-[2.2px] uppercase text-sidebar-foreground/30">
                {category.label}
              </SidebarGroupLabel>
            )}
            
            <SidebarGroupContent>
              <SidebarMenu className="gap-0 space-y-px">
              {category.items.map((item) => {
                  const isActive = isItemActive(item.url);
                  const badgeValue = getBadgeValue(item.badgeKey);
                  
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild className="h-auto p-0">
                        <NavLink
                          to={item.url}
                          className={cn(
                            "flex items-center gap-2 px-[9px] rounded-lg transition-all duration-150 group relative",
                            isActive
                              ? "py-[5px] bg-background text-primary dark:text-foreground font-bold shadow-[0_2px_8px_rgba(0,0,0,0.30),inset_0_1px_0_#FFF] dark:shadow-[0_2px_8px_rgba(0,0,0,0.30)] dark:border-l-[3px] dark:border-primary"
                              : "py-1 text-sidebar-foreground/[0.68] hover:text-sidebar-foreground hover:bg-sidebar-accent/[0.07]"
                          )}
                        >
                          {/* Ícone */}
                          <item.icon
                            strokeWidth={2}
                            className={cn(
                              "h-3.5 w-3.5 flex-shrink-0",
                              isActive ? "text-primary dark:text-foreground" : "text-sidebar-foreground opacity-[0.62]"
                            )}
                          />

                          {/* Título */}
                          {!collapsed && (
                            <span className={cn("flex-1 text-[11.5px] leading-[1.3] tracking-[-0.05px]", isActive && "font-bold")}>
                              {item.title}
                            </span>
                          )}
                          
                          {/* Badge */}
                          {!collapsed && badgeValue && (
                            <Badge 
                              variant="secondary" 
                              className={cn(
                                "ml-auto text-[8.5px] font-bold px-1.5 py-0 h-[15px] min-w-[15px] rounded-[7px] flex items-center justify-center",
                                item.badgeKey === 'filaAtiva'
                                  ? "bg-success-soft/20 text-success-soft"
                                  : isActive
                                    ? "bg-primary/15 text-primary"
                                    : "bg-sidebar-foreground/[0.13] text-sidebar-foreground"
                              )}
                            >
                              {badgeValue}
                            </Badge>
                          )}
                          
                          {/* Badge compacto para collapsed */}
                          {collapsed && badgeValue && (
                            <span 
                              className={cn(
                                "absolute top-0.5 right-0.5 h-2 w-2 rounded-full",
                                item.badgeKey === 'filaAtiva' ? "bg-success-soft" : "bg-sidebar-primary"
                              )}
                            />
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="p-2 border-t border-sidebar-border flex-shrink-0">
        {/* Perfil do Usuário */}
        {!collapsed && profile && (
          <NavLink to="/meu-perfil" className="block mb-1.5">
            <div className="flex items-center gap-2 p-1.5 rounded-[10px] bg-sidebar-accent/[0.07] hover:bg-sidebar-accent/[0.12] transition-all duration-200 group">
              <Avatar className="h-[25px] w-[25px]">
                {profile.foto_url && (
                  <AvatarImage src={profile.foto_url} alt={profile.nome} />
                )}
                <AvatarFallback className="bg-gradient-to-br from-brand-rose to-brand-mid text-white font-bold text-[10px]">
                  {getInitials(profile.nome)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-sidebar-foreground truncate">
                  {profile.nome}
                </p>
                <p className="text-[9px] text-sidebar-foreground/60 lowercase">
                  {profile.role?.toLowerCase().replace('_', ' ')}
                </p>
              </div>
            </div>
          </NavLink>
        )}

        {/* Avatar compacto quando collapsed */}
        {collapsed && profile && (
          <NavLink to="/meu-perfil" className="flex justify-center mb-1.5">
            <div className="relative">
              <Avatar className="h-7 w-7 hover:scale-105 transition-transform">
                {profile.foto_url && (
                  <AvatarImage src={profile.foto_url} alt={profile.nome} />
                )}
                <AvatarFallback className="bg-gradient-to-br from-sidebar-primary to-sidebar-primary/60 text-white font-bold text-[9px]">
                  {getInitials(profile.nome)}
                </AvatarFallback>
              </Avatar>
              {conexaoSaude.caiu && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-danger border border-sidebar-border"></span>
                </span>
              )}
            </div>
          </NavLink>
        )}

        {/* Status das conexoes (primaria/secundaria/reserva) + Reconectar + Sair — mesma fonte/cores do painel */}
        <div className={cn(
          "flex items-center pt-1.5 border-t border-white/[0.07]",
          collapsed ? "flex-col gap-1.5" : "justify-between gap-2"
        )}>
          <TooltipProvider delayDuration={150}>
            <div className={cn("flex items-center gap-1.5", collapsed && "flex-col gap-1.5")}>
              {([
                { papel: "primaria" as const, rotulo: "1", nomePapel: "Primaria" },
                { papel: "secundaria" as const, rotulo: "2", nomePapel: "Secundaria" },
                { papel: "reserva" as const, rotulo: "R", nomePapel: "Reserva da equipe" },
              ]).map((slot) => {
                const c = conexaoSaude.conexoes.find((x) => x.papel === slot.papel);
                const estado = !c ? "Nao configurada"
                  : c.status === "fora" ? "Caida - reconecte pelo QR"
                  : c.status === "instavel" ? "Instavel"
                  : c.emUso ? "Em uso agora"
                  : c.status === "conectada" ? "Disponivel (em espera)"
                  : "Verificando";
                const classes = !c
                  ? "text-sidebar-foreground/25 border border-dashed border-sidebar-foreground/25"
                  : c.status === "fora"
                  ? "text-danger bg-danger/15 border border-danger/60"
                  : c.status === "instavel"
                  ? "text-warning border border-warning/50"
                  : c.emUso
                  ? "text-[#7ECFA0] bg-[#7ECFA0]/10 border border-[#7ECFA0]/45"
                  : c.status === "conectada"
                  ? "text-sidebar-foreground/45 border border-sidebar-foreground/25"
                  : "text-sidebar-foreground/30 border border-sidebar-foreground/20";
                const mostraEmUso = !!c?.emUso && !collapsed;
                return (
                  <Tooltip key={slot.papel}>
                    <TooltipTrigger asChild>
                      <span className={cn("relative inline-flex h-5 items-center justify-center rounded text-[10px] font-semibold cursor-default select-none", mostraEmUso ? "gap-1 px-1.5" : "w-5", classes)}>
                        {slot.rotulo}
                        {mostraEmUso && <span className="text-[8px] font-bold tracking-wide">EM USO</span>}
                        {c?.emUso && (
                          <span className="absolute -top-0.5 -right-0.5 flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-success"></span>
                          </span>
                        )}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs max-w-[220px]">
                      <span className="font-medium">{slot.nomePapel}</span>{c?.nome ? " - " + c.nome : ""}
                      <br />{estado}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          <div className={cn("flex items-center gap-1", collapsed && "flex-col")}>
            {conexaoSaude.caiu && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setReconnectOpen(true)}
                title="Reconectar WhatsApp"
                className="h-7 w-7 text-sidebar-foreground hover:bg-sidebar-foreground/10"
              >
                <QrCode className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size={collapsed ? "icon" : "sm"}
              onClick={handleSignOut}
              className={cn(
                "text-sidebar-foreground/70 hover:text-[#E88A8A] hover:bg-white/[0.06] transition-colors",
                collapsed ? "h-7 w-7" : "h-7"
              )}
            >
              <LogOut className="h-3.5 w-3.5" />
              {!collapsed && <span className="ml-1.5 text-xs">Sair</span>}
            </Button>
          </div>
        </div>
      </SidebarFooter>
      {reconnectOpen && conexaoSaude.primariaInstanceName && (
        <Suspense fallback={null}>
          <EvolutionConnectionDialog
            open={reconnectOpen}
            onOpenChange={setReconnectOpen}
            reconnectInstanceName={conexaoSaude.primariaInstanceName}
          />
        </Suspense>
      )}
    </Sidebar>
  );
}
